import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef, useEffect, useState } from 'react';
import {
  ArrowRight, PhoneCall, ShieldCheck, Star, ArrowUpRight, User, Users, CheckCircle
} from '@phosphor-icons/react';
import { Reveal, SplitReveal } from '@/components/motion';
import { Marquee, CountUp, ScrollFloat, RouteLine, MaskReveal } from '@/components/graphics';
import { SERVICES, IMAGES, CONTACT } from '@/data/site';
import { public_api } from '@/lib/api';
import { useContent } from '@/context/ContentContext';

const PHOTOS = {
  hero: 'https://images.pexels.com/photos/35831410/pexels-photo-35831410.jpeg?auto=compress&cs=tinysrgb&w=1600',
  caregiver: 'https://images.pexels.com/photos/18509794/pexels-photo-18509794.jpeg?auto=compress&cs=tinysrgb&w=1200',
  groceries: 'https://images.pexels.com/photos/7345443/pexels-photo-7345443.jpeg?auto=compress&cs=tinysrgb&w=1200',
  chauffeur: 'https://images.pexels.com/photos/36377051/pexels-photo-36377051.jpeg?auto=compress&cs=tinysrgb&w=1200',
};

const SERVICE_PREVIEWS = [PHOTOS.hero, PHOTOS.caregiver, PHOTOS.groceries, PHOTOS.chauffeur, IMAGES.hero, IMAGES.support_care];

/* ---------------------------------- HERO ---------------------------------- */
function Hero() {
  const ref = useRef(null);
  const c = useContent();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  const headline = c('home.hero.headline', 'Reliable transport. Real independence. Everywhere in Australia.');
  const sentences = (headline.match(/[^.!?]+[.!?]*/g) || [headline]).map(s => s.trim()).filter(Boolean);

  return (
    <section ref={ref} className="relative pt-10 md:pt-20 pb-16 md:pb-24 overflow-hidden" data-testid="home-hero">
      <div className="container-x relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          {/* Editorial left column */}
          <div className="lg:col-span-7 relative">
            <Reveal delay={0.05}>
              <div className="flex flex-wrap items-center gap-3 mb-8">
                <span className="inline-flex items-center gap-2 border border-euca/40 text-euca-deep rounded-full px-4 py-1.5 text-xs font-bold tracking-[0.14em] uppercase" data-testid="hero-eyebrow">
                  <span className="w-1.5 h-1.5 rounded-full bg-euca animate-pulse" />
                  {c('home.hero.eyebrow', "Australia's community transport partner")}
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink-muted" data-testid="hero-ndis-badge">
                  <ShieldCheck size={14} className="text-brand" /> NDIS registration in progress
                </span>
              </div>
            </Reveal>

            <h1 className="h-display text-balance" data-testid="hero-headline">
              {sentences.map((s, i) => (
                <span key={s} className={i === sentences.length - 1 ? 'italic text-brand' : undefined}>
                  <SplitReveal text={s} delay={i * 0.3} />{' '}
                </span>
              ))}
            </h1>

            <Reveal delay={1.0} className="mt-8 max-w-xl">
              <p className="body-lg text-pretty" data-testid="hero-subheadline">
                {c('home.hero.subheadline', 'Inclusive Mobility Group is a national community transport provider — NDIS registration in progress — connecting participants, families and health services with safe, dignified transport, every day of the year.')}
              </p>
            </Reveal>

            {/* Dual-path chooser */}
            <Reveal delay={1.15} className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
              <Link to="/book" data-testid="hero-path-self"
                className="group flex items-center justify-between border border-ink/15 bg-white/70 hover:border-brand px-6 py-5 transition-colors duration-300 rounded-sm">
                <span className="flex items-center gap-3">
                  <User size={22} weight="duotone" className="text-brand" />
                  <span className="font-semibold text-ink">I'm booking<br className="sm:hidden" /> for myself</span>
                </span>
                <ArrowUpRight size={18} className="text-ink-muted group-hover:text-brand group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
              </Link>
              <Link to="/book" data-testid="hero-path-support"
                className="group flex items-center justify-between border border-ink/15 bg-white/70 hover:border-brand px-6 py-5 transition-colors duration-300 rounded-sm">
                <span className="flex items-center gap-3">
                  <Users size={22} weight="duotone" className="text-euca-deep" />
                  <span className="font-semibold text-ink">For someone<br className="sm:hidden" /> I support</span>
                </span>
                <ArrowUpRight size={18} className="text-ink-muted group-hover:text-brand group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
              </Link>
            </Reveal>

            <Reveal delay={1.3} className="mt-8 flex flex-wrap items-center gap-5">
              <Link to="/quote" data-testid="hero-cta-quote" className="btn-ghost text-base underline decoration-brand/40 underline-offset-4 hover:decoration-brand">
                Get an instant quote
              </Link>
              <a href={`tel:${CONTACT.phone_dial}`} className="inline-flex items-center gap-2 font-semibold text-ink" data-testid="hero-cta-call">
                <span className="w-9 h-9 rounded-full bg-brand/10 flex items-center justify-center">
                  <PhoneCall size={16} weight="bold" className="text-brand" />
                </span>
                {CONTACT.phone_display} <span className="text-ink-muted font-normal">· 24/7</span>
              </a>
            </Reveal>
          </div>

          {/* Photography right column */}
          <div className="lg:col-span-5 relative">
            <ScrollFloat from={20} to={-30} className="absolute -top-8 -left-10 z-10 hidden lg:block text-brand">
              <RouteLine
                viewBox="0 0 200 300"
                d="M180 10 C 100 60, 160 150, 60 200 S 20 270, 30 295"
                dots={[[180, 10], [30, 295]]}
                className="w-[200px] h-[300px]"
                strokeWidth={2}
              />
            </ScrollFloat>
            <MaskReveal delay={0.3}>
              <motion.div style={{ y: imgY }} className="relative">
                <img
                  src={PHOTOS.hero}
                  alt="A passenger boarding a wheelchair accessible vehicle with dignity"
                  className="w-full aspect-[4/5] object-cover rounded-sm"
                />
                <div className="absolute bottom-5 left-5 right-5 bg-porcelain/90 backdrop-blur-md p-5 border-l-2 border-brand rounded-sm">
                  <div className="font-display italic text-xl text-ink">"Door-to-door dignity, every single trip."</div>
                  <div className="text-xs text-ink-muted mt-2 tracking-widest uppercase font-semibold">The Inclusive Mobility Group standard</div>
                </div>
              </motion.div>
            </MaskReveal>
            <Reveal delay={0.7} className="hidden md:flex items-center gap-6 mt-6 justify-end">
              {['Safe', 'Compassionate', 'On time'].map((w) => (
                <span key={w} className="flex items-center gap-1.5 text-sm text-ink-soft">
                  <CheckCircle size={15} weight="fill" className="text-euca" /> {w}
                </span>
              ))}
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------- MARQUEE --------------------------------- */
function ServiceMarquee() {
  const names = ['NDIS Transport', 'Aged Care', 'Hospital Transport', 'TAC & WorkCover', 'Airport Transfers', 'Community Participation', 'Medical Appointments', 'Wheelchair Accessible'];
  return (
    <section className="py-8 border-y border-ink/10 overflow-hidden" data-testid="home-city-marquee">
      <Marquee duration={46}>
        {names.map(n => (
          <span key={n} className="flex items-center">
            <span className="font-display italic font-light text-3xl md:text-4xl text-ink/70 whitespace-nowrap px-8">{n}</span>
            <span className="w-2 h-2 rounded-full bg-brand shrink-0" />
          </span>
        ))}
      </Marquee>
    </section>
  );
}

/* --------------------------- SERVICES INDEX LIST --------------------------- */
function ServicesIndex() {
  const [active, setActive] = useState(0);
  return (
    <section id="services" className="py-24 md:py-36" data-testid="home-services">
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="overline mb-6">What we do</div>
              <h2 className="h-section text-balance">One provider. <span className="italic text-brand">Every kind</span> of transport a life takes.</h2>
            </Reveal>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 flex items-end">
            <Reveal delay={0.15}>
              <p className="body-md text-pretty">From NDIS core transport to a 2am hospital discharge — ten integrated services under one national operating standard.</p>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Index list */}
          <div className="lg:col-span-7 border-t border-ink/10" data-testid="services-bento">
            {SERVICES.map((s, i) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                onMouseEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                className="group flex items-baseline gap-6 border-b border-ink/10 py-6 md:py-7 hover:bg-white/60 transition-colors duration-300 px-2 -mx-2 rounded-sm"
              >
                <span className="font-mono text-xs text-ink-muted w-8 shrink-0">{String(i + 1).padStart(2, '0')}</span>
                <span className="flex-1">
                  <span className="font-display text-2xl md:text-3xl text-ink group-hover:text-brand transition-colors duration-300">{s.name}</span>
                  <span className="hidden md:block text-sm text-ink-muted mt-1 max-w-md leading-relaxed">{s.tagline}</span>
                </span>
                <ArrowRight size={20} className="text-ink-muted group-hover:text-brand group-hover:translate-x-1 transition-transform duration-300 shrink-0 self-center" />
              </Link>
            ))}
            <div className="mt-8">
              <Link to="/services" data-testid="home-services-all-btn" className="btn-secondary">
                View all services <ArrowRight size={16} weight="bold" />
              </Link>
            </div>
          </div>

          {/* Giant sticky preview */}
          <div className="hidden lg:block lg:col-span-5 relative">
            <div className="sticky top-28 aspect-[4/5] overflow-hidden rounded-sm" data-testid="services-preview">
              {SERVICES.map((s, i) => (
                <img
                  key={s.slug}
                  src={SERVICE_PREVIEWS[i % SERVICE_PREVIEWS.length]}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
                  style={{ opacity: active === i ? 1 : 0 }}
                />
              ))}
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-ink/80 to-transparent">
                <div className="text-porcelain font-display italic text-2xl">{SERVICES[active]?.name}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------- ENTERPRISE BAND ------------------------------ */
function EnterpriseBand() {
  const stats = [
    { to: 10, suffix: '', label: 'Integrated transport services' },
    { to: 8, suffix: '', label: 'States & territories covered' },
    { to: 24, suffix: '/7', label: 'Dispatch & live support' },
    { to: 98, suffix: '%', label: 'On-time pickup target' },
  ];
  return (
    <section className="py-24 md:py-32 bg-ink text-porcelain relative overflow-hidden" data-testid="home-stats">
      <div className="absolute inset-0 opacity-25 noise-overlay pointer-events-none" />
      <ScrollFloat from={50} to={-50} className="absolute -top-24 -right-24 pointer-events-none">
        <div className="w-96 h-96 rounded-full bg-brand/15 blur-3xl" />
      </ScrollFloat>
      <div className="container-x relative">
        <div className="flex flex-wrap items-end justify-between gap-6 pb-12 border-b border-porcelain/15">
          <Reveal>
            <div className="text-xs font-bold tracking-[0.22em] uppercase text-brand-soft mb-4">Operations, measured</div>
            <h2 className="h-section text-porcelain max-w-2xl text-balance">An enterprise operation with a <span className="italic">human</span> job description.</h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-porcelain/60 max-w-sm text-base leading-relaxed">
              Live dispatch, vetted drivers, compliance-tracked vehicles and full audit trails — the machinery stays invisible so the care isn't.
            </p>
          </Reveal>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-12 pt-12">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <div className={`px-2 md:px-8 ${i > 0 ? 'lg:border-l border-porcelain/15' : ''}`}>
                <div className="font-display font-light text-6xl md:text-7xl leading-none">
                  <CountUp to={s.to} suffix={s.suffix} />
                </div>
                <div className="text-porcelain/55 text-sm mt-4">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.3}>
          <div className="mt-14 pt-8 border-t border-porcelain/15 flex flex-wrap items-center gap-x-10 gap-y-3 text-sm text-porcelain/55">
            <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-brand-soft" /> NDIS registration in progress</span>
            <span className="flex items-center gap-2"><CheckCircle size={16} className="text-brand-soft" /> NDIS Worker Screened drivers</span>
            <span className="flex items-center gap-2"><CheckCircle size={16} className="text-brand-soft" /> First-aid certified</span>
            <span className="flex items-center gap-2"><CheckCircle size={16} className="text-brand-soft" /> Hoist-inspected fleet</span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ------------------------------ FLEET EDITORIAL ---------------------------- */
function FleetEditorial() {
  const [vehicles, setVehicles] = useState([]);
  useEffect(() => { public_api.fleet().then(v => setVehicles(v.slice(0, 3))).catch(() => {}); }, []);
  if (!vehicles.length) return null;
  return (
    <section className="py-24 md:py-36" data-testid="home-fleet">
      <div className="container-x">
        <div className="max-w-3xl mb-16">
          <Reveal>
            <div className="overline mb-6">The fleet</div>
            <h2 className="h-section text-balance">Accessibility, specified like <span className="italic text-brand">luxury.</span></h2>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {vehicles.map((v, i) => (
            <MaskReveal key={v.id} delay={i * 0.12}>
              <Link to="/fleet" className="group block" data-testid={`home-fleet-card-${v.id}`}>
                <div className="aspect-[4/3] overflow-hidden rounded-sm bg-neutral-100">
                  <img src={v.image_url} alt={v.name} className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" />
                </div>
                <div className="pt-5 border-t border-ink/10 mt-5 flex items-baseline justify-between gap-4">
                  <div>
                    <div className="font-display text-xl text-ink group-hover:text-brand transition-colors duration-300">{v.name}</div>
                    <div className="text-sm text-ink-muted mt-1">{v.type} · Capacity {v.capacity}</div>
                  </div>
                  <ArrowUpRight size={18} className="text-ink-muted group-hover:text-brand shrink-0 transition-colors" />
                </div>
                {v.features?.length > 0 && (
                  <div className="text-xs text-ink-muted mt-2">{v.features.slice(0, 2).join(' · ')}</div>
                )}
              </Link>
            </MaskReveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------- TESTIMONIALS ------------------------------ */
function Testimonials() {
  const [items, setItems] = useState([]);
  useEffect(() => { public_api.testimonials(true).then(setItems).catch(() => setItems([])); }, []);
  if (!items.length) return null;
  const [first, ...rest] = items;
  return (
    <section className="py-24 md:py-36 bg-white border-y border-ink/5" data-testid="home-testimonials">
      <div className="container-x">
        <Reveal>
          <div className="overline mb-10">In their own words</div>
          <blockquote className="font-display italic font-light text-3xl md:text-5xl leading-[1.2] text-ink max-w-4xl text-balance">
            "{first.quote}"
          </blockquote>
          <div className="mt-8 flex items-center gap-4">
            <div className="flex gap-0.5">
              {Array.from({ length: first.rating || 5 }).map((_, k) => <Star key={k} size={16} weight="fill" className="text-brand" />)}
            </div>
            <div className="text-sm text-ink-soft"><span className="font-semibold text-ink">{first.author_name}</span> · {first.author_role}, {first.author_location}</div>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-16 pt-12 border-t border-ink/10">
          {rest.slice(0, 2).map((t, i) => (
            <Reveal key={t.id} delay={i * 0.12}>
              <blockquote className="font-display italic text-xl md:text-2xl text-ink-soft leading-relaxed">"{t.quote}"</blockquote>
              <div className="text-sm text-ink-muted mt-4">{t.author_name} · {t.author_role}</div>
            </Reveal>
          ))}
        </div>
        <div className="mt-12">
          <Link to="/testimonials" className="btn-ghost underline decoration-brand/40 underline-offset-4 hover:decoration-brand">Read every review →</Link>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- CTA ------------------------------------- */
function JourneyCTA() {
  return (
    <section className="py-24 md:py-36 relative overflow-hidden" data-testid="home-cta">
      <RouteLine
        viewBox="0 0 1200 160"
        d="M10 140 C 250 40, 500 150, 720 70 S 1050 20, 1190 90"
        dots={[[10, 140], [1190, 90]]}
        className="absolute top-6 left-0 w-full h-[160px] text-brand/40 hidden md:block pointer-events-none"
        strokeWidth={2}
      />
      <div className="container-x relative text-center max-w-3xl mx-auto">
        <Reveal>
          <h2 className="h-section text-balance">Wherever life needs you next, <span className="italic text-brand">we'll get you there.</span></h2>
          <p className="body-lg mt-6">Book online in under two minutes, or talk to a real coordinator any hour of the day.</p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/book" data-testid="home-cta-book" className="btn-primary !px-9 !py-4">Book transport <ArrowRight size={18} weight="bold" /></Link>
            <Link to="/quote" data-testid="home-cta-quote" className="btn-secondary !px-9 !py-4">Instant quote</Link>
          </div>
          <a href={`tel:${CONTACT.phone_dial}`} className="inline-flex items-center gap-2 mt-8 text-ink-soft hover:text-brand transition-colors">
            <PhoneCall size={16} weight="bold" className="text-brand" /> {CONTACT.phone_display} — 24 hours, 7 days
          </a>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <ServiceMarquee />
      <ServicesIndex />
      <EnterpriseBand />
      <FleetEditorial />
      <Testimonials />
      <JourneyCTA />
    </>
  );
}
